import React, { useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import {
  COMPANY_INFO,
  BENEFITS,
  VINDUER_PRICES,
  VINDUER_DISCOUNTS
} from "../data/siteData";
import FliserensCalculator from "../components/FliserensCalculator";
import ReviewSlider from "../components/ReviewSlider";
import CmsGallery from "../components/CmsGallery";
import ContactForm from "../components/ContactForm";

export default function HomePage() {
  const [calculationData, setCalculationData] = useState(null);

  return (
    <>
      <Helmet>
        <title>Vinduespolering i Slagelse, Sorø &amp; Skælskør | NF-Polering</title>
        <meta
          name="description"
          content="Professionel vinduespudser på Vestsjælland. NF-Polering tilbyder vinduespolering til private i Slagelse, Sorø, Skælskør, høj kvalitet og hurtig service"
        />
        <meta
          property="og:title"
          content="Vinduespolering i Slagelse, Sorø &amp; Skælskør | NF-Polering"
        />
        <meta
          property="og:description"
          content="Professionel vinduespudser på Vestsjælland. NF-Polering tilbyder vinduespolering til private i Slagelse, Sorø, Skælskør, høj kvalitet og hurtig service"
        />
        <meta
          property="og:image"
          content="https://assets.auxodev.dk/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/gemini-generated-image-3jxk3t3jxk3t3jxk-e1763482441514.png"
        />
        <link rel="canonical" href="https://nf-polering.dk/" />
      </Helmet>

      {/* Hero Section */}
      <section className="relative min-h-[650px] flex items-center bg-slate-950 text-white overflow-hidden py-12 sm:py-16 lg:py-20">
        {/* Full width background image with smooth entry zoom */}
        <div className="absolute inset-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/gemini-generated-image-3jxk3t3jxk3t3jxk-e1763482441514.png"
            alt="NF-Polering vinduespudser i Slagelse og på Vestsjælland"
            className="w-full h-full object-cover object-center animate-hero-zoom"
          />
          {/* High-contrast gradient overlay with lighter right side */}
          <div className="absolute inset-0 bg-gradient-to-r from-slate-950/90 via-slate-950/55 to-slate-950/20" />
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
            {/* Left Column: Headline & Info */}
            <div className="lg:col-span-6 space-y-6 text-left">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight text-white leading-tight">
                Vinduespudser i Slagelse og på Vestsjælland
              </h1>

              <p className="text-base sm:text-lg text-slate-200 leading-relaxed">
                Få hurtig tid, skarpe priser og et resultat der holder. Ring nu eller udfyld formularen – så får du din pris med det samme.
              </p>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-start gap-4 pt-1">
                <a
                  href={COMPANY_INFO.phoneTel}
                  className="inline-flex items-center justify-center gap-3 px-7 py-3.5 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-base transition-all transform active:scale-95 whitespace-nowrap shadow-lg shadow-cyan-500/25"
                >
                  <i className="fa-solid fa-phone" aria-hidden="true" />
                  <span className="whitespace-nowrap">{COMPANY_INFO.phone}</span>
                </a>
              </div>

              {/* Trust Indicators */}
              <div className="pt-6 border-t border-white/20 grid grid-cols-3 gap-4 sm:gap-6 text-left max-w-lg">
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">Fra 99,-</div>
                  <div className="text-xs text-slate-300">Gennemsigtige priser</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">100%</div>
                  <div className="text-xs text-slate-300">Tilfredshedsgaranti</div>
                </div>
                <div>
                  <div className="text-xl sm:text-2xl font-extrabold text-cyan-400">4-12 uger</div>
                  <div className="text-xs text-slate-300">Fast abonnement</div>
                </div>
              </div>
            </div>

            {/* Right Column: Webform */}
            <div className="lg:col-span-6">
              <ContactForm
                id="tilbud"
                title="Udfyld formularen og få et tilbud"
                subtitle="Vi svarer hurtigt med en skarp pris uden skjulte gebyrer."
                variant="hero"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-left mb-10 pb-6 border-b border-slate-200">
            <h2 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-slate-900 tracking-tight">
              Hvorfor vælge NF-Polering?
            </h2>
            <p className="mt-2 text-slate-600 text-base">
              Lokal vinduespudser med fokus på kvalitet og faste, gennemsigtige priser
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-8">
            {BENEFITS.map((benefit, i) => (
              <div
                key={i}
                className="text-center flex flex-col items-center justify-start p-2"
              >
                <div className="w-12 h-12 rounded-full bg-cyan-50 text-cyan-600 flex items-center justify-center text-xl mb-3 shrink-0">
                  <i className={`fa-solid ${benefit.icon}`} aria-hidden="true" />
                </div>
                <h3 className="font-bold text-slate-900 text-lg">
                  {benefit.title}
                </h3>
                {benefit.desc && (
                  <p className="mt-1.5 text-slate-600 text-sm leading-relaxed">
                    {benefit.desc}
                  </p>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About NF-Polering Section */}
      <section id="om-os" className="py-16 sm:py-24 bg-slate-50 border-b border-slate-200 scroll-mt-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 space-y-6">
              <h2 className="text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
                Vinduespudser i Slagelse og på Vestsjælland
              </h2>
              <div className="space-y-3 text-slate-700 text-base leading-relaxed">
                <p>
                  Hos NF-Polering tilbyder vi professionel vinduespolering og fliserens til private og erhverv i hele Vestsjælland.
                </p>
                <p>
                  Vi sikrer skinnende rene ruder til faste, gennemsigtige priser – hvad enten du ønsker en fast abonnementsaftale eller et enkeltbesøg.
                </p>
              </div>

              <div className="pt-2 flex flex-wrap items-center gap-3">
                <Link
                  to="/vinduespudsning"
                  className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm transition-colors"
                >
                  <span>Læs mere</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <a
                  href="#kontakt"
                  className="inline-flex items-center gap-2 text-slate-900 hover:text-cyan-600 font-bold text-sm transition-colors px-2 py-3.5"
                >
                  <span>Få et tilbud</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </a>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-none overflow-hidden shadow-xl border border-slate-200">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/vestsja-lland_98e2ee4c.png"
                  alt="Vestsjælland skilt – NF Polering dækker hele Vestsjælland"
                  className="w-full h-96 sm:h-[460px] lg:h-[520px] object-cover object-[center_20%]"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Traditionel Vinduespudsning Priser */}
      <section className="py-16 sm:py-24 bg-white scroll-mt-24" id="priser">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12 sm:mb-16">
            <h2 className="text-2xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">
              Traditionel Vinduespudsning
            </h2>
            <p className="mt-3 text-slate-600 text-base">
              Gennemsigtige priser – så du ved præcis hvad det koster
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 items-stretch">
            {VINDUER_PRICES.map((priceCard, idx) => (
              <div
                key={idx}
                className="relative rounded-none p-7 sm:p-8 flex flex-col justify-between transition-all bg-white border border-slate-200 shadow-sm"
              >
                <div>
                  <h3 className="text-xl sm:text-2xl font-extrabold text-slate-900 mb-4">
                    {priceCard.title}
                  </h3>

                  <div className="flex items-baseline gap-1.5 mb-4 pb-4 border-b border-slate-100">
                    <span className="text-3xl sm:text-4xl font-black text-slate-900">
                      {priceCard.price}
                    </span>
                    <span className="text-sm font-semibold text-slate-500">
                      {priceCard.unit}
                    </span>
                  </div>

                  <p className="text-xs sm:text-sm text-slate-600 mb-6 italic">
                    {priceCard.note}
                  </p>

                  <ul className="space-y-3 mb-8 text-sm text-slate-700">
                    {priceCard.features.map((feat, fIdx) => (
                      <li key={fIdx} className="flex items-start gap-2.5">
                        <i className="fa-solid fa-check text-cyan-600 text-sm mt-0.5 shrink-0" aria-hidden="true" />
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>
                </div>

                <div>
                  <a
                    href={COMPANY_INFO.phoneTel}
                    className="w-full py-3.5 px-4 rounded-full font-bold text-sm text-center transition-all flex items-center justify-center gap-2 bg-slate-900 hover:bg-slate-800 text-white"
                  >
                    <span>FÅ TILBUD</span>
                    <i className="fa-solid fa-phone text-xs" aria-hidden="true" />
                  </a>
                </div>
              </div>
            ))}
          </div>

          <p className="text-center text-xs text-slate-500 mt-8 max-w-2xl mx-auto">
            Alle priser er fra-priser og gælder almindelige et-plans huse. Særlige adgangsforhold, meget snavsede ruder eller ekstra arbejde kan medføre tillæg.
          </p>
        </div>
      </section>

      {/* Fliserens Section with Interactive Calculator */}
      <section className="py-16 sm:py-24 bg-slate-50 border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center mb-16">
            <div className="lg:col-span-6 space-y-5">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
                Fliserens fra kun 29 kr. pr. m² inkl. imprægnering
              </h2>
              <p className="text-slate-600 text-base leading-relaxed">
                Er dine fliser blevet mørke, grønne eller fyldt med alger og belægninger? Hos NF-Polering hjælper vi med at få dine udendørs fliser til at fremstå flotte og velholdte igen.
              </p>
              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Fliserens</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Algebehandling</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Imprægnering</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Kun 29 kr. pr. m²</span>
                </div>
              </div>
              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/fliserens"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm transition-colors"
                >
                  <span>Læs mere om fliserens</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
              </div>
            </div>

            <div className="lg:col-span-6">
              <div className="rounded-none overflow-hidden shadow-xl border border-slate-200 bg-slate-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fliserens-have-terrasse-ren.png"
                  alt="Professionel Fliserens af terrasse og indkørsel"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>
          </div>

          {/* Embedded Interactive Fliserens Calculator */}
          <FliserensCalculator onSelectCalculation={(data) => setCalculationData(data)} />
        </div>
      </section>

      {/* Solcellevask Highlight Section */}
      <section className="py-16 sm:py-24 bg-white border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6 order-2 lg:order-1">
              <div className="rounded-2xl overflow-hidden shadow-xl border border-slate-200 bg-slate-100">
                <img
                  src="https://assets.auxodev.dk/cdn-cgi/image/width=900,quality=85,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/hero-solcellevask-nf-polering.png"
                  alt="Professionel solcellevask på Vestsjælland - NF-Polering"
                  className="w-full h-80 sm:h-96 object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-6 space-y-5 order-1 lg:order-2">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-slate-900 tracking-tight">
                Solcellevask – Få maksimalt udbytte af solen
              </h2>

              <p className="text-slate-600 text-base leading-relaxed">
                Vidste du, at snavs, pollen og fugleklatter kan koste dig op mod 25% af din årlige strømproduktion? Med professionel vask i ultra-rent osmosevand sikrer vi, at dine solcellepaneler yder optimalt hele året.
              </p>

              <div className="grid grid-cols-2 gap-3 pt-2">
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>22 kr. pr. panel</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Halvårs- &amp; årsabonnement</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>100% kalk- &amp; kemifrit</span>
                </div>
                <div className="flex items-center gap-2 text-sm font-semibold text-slate-800">
                  <i className="fa-solid fa-check text-cyan-600" aria-hidden="true" />
                  <span>Ingen binding</span>
                </div>
              </div>

              <div className="pt-4 flex flex-wrap gap-4">
                <Link
                  to="/solcellevask"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-cyan-600 hover:bg-cyan-700 text-white font-bold text-sm transition-colors"
                >
                  <span>Læs mere om solcellevask</span>
                  <i className="fa-solid fa-arrow-right text-xs" aria-hidden="true" />
                </Link>
                <Link
                  to="/solcellevask#beregner"
                  className="inline-flex items-center gap-2 px-6 py-3.5 rounded-full bg-slate-100 hover:bg-slate-200 text-slate-900 font-bold text-sm border border-slate-300 transition-colors"
                >
                  <i className="fa-solid fa-calculator text-cyan-600" aria-hidden="true" />
                  <span>Beregn din pris</span>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Subscription Discount Section */}
      <section id="abonnement" className="relative py-16 sm:py-24 bg-slate-900 text-white overflow-hidden scroll-mt-24">
        {/* Background image & overlay */}
        <div className="absolute inset-0 z-0 overflow-hidden">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/fullsizerender_04929841.jpeg"
            alt="Abonnement på vinduespudsning – NF Polering"
            className="w-full h-full object-cover object-center animate-hero-zoom"
          />
          <div className="absolute inset-0 bg-slate-950/80 backdrop-blur-[1px]" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-cyan-500/20 text-cyan-300 text-sm font-semibold tracking-wide uppercase mb-3 border border-cyan-400/30">
              Abonnementsløsning
            </span>
            <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
              Spar penge på rene vinduer
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 divide-y md:divide-y-0 md:divide-x divide-white/60 w-full">
            {VINDUER_DISCOUNTS.map((disc, idx) => (
              <div
                key={idx}
                className="text-center py-6 md:py-8 px-6 flex flex-col items-center justify-center"
              >
                {disc.tag && (
                  <span className="text-xs sm:text-sm font-semibold uppercase tracking-wider text-cyan-400 mb-2">
                    {disc.tag}
                  </span>
                )}
                <h3 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold text-white tracking-tight">
                  {disc.frequency}
                </h3>
              </div>
            ))}
          </div>

          <div className="text-center mt-10">
            <a
              href="#kontakt"
              className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-base transition-colors"
            >
              <span>Vælg din abonnementsløsning</span>
              <i className="fa-solid fa-arrow-right text-sm" aria-hidden="true" />
            </a>
          </div>
        </div>
      </section>

      {/* Dynamic CMS Gallery */}
      <CmsGallery
        slug="vinduespudsning"
        title="Billeder af vores arbejde"
        subtitle="Se eksempler på vores arbejde med vinduespolering og fliserens på Vestsjælland."
      />

      {/* Customer Reviews Infinite Slider */}
      <ReviewSlider />

      {/* Contact Section */}
      <section className="relative py-16 sm:py-24 bg-slate-950 text-white text-center overflow-hidden scroll-mt-24" id="kontakt">
        {/* Background image & overlay */}
        <div className="absolute inset-0 z-0">
          <img
            src="https://assets.auxodev.dk/cdn-cgi/image/width=1920,quality=80,format=auto/assets/e7be4d2d-7d84-4f18-944c-35dc3bc1037c/galleri-rene-vinduer-stue.png"
            alt="Rene vinduer og vinduespudsning"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-slate-950/85 backdrop-blur-[1px]" />
        </div>

        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 space-y-6">
          <h2 className="text-2xl sm:text-4xl font-extrabold tracking-tight text-white">
            Klar til skinnende rene vinduer?
          </h2>
          <p className="text-slate-300 text-base sm:text-lg max-w-2xl mx-auto">
            Få et uforpligtende tilbud i dag eller ring direkte til os på {COMPANY_INFO.phone}. Vi står klar til at hjælpe dig på hele Vestsjælland.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 pt-2">
            <a
              href={COMPANY_INFO.phoneTel}
              className="px-8 py-4 rounded-full bg-cyan-500 hover:bg-cyan-400 text-white font-bold text-base transition-all transform active:scale-95"
            >
              Få et tilbud – Ring {COMPANY_INFO.phone}
            </a>
            <Link
              to="/vinduespudsning#kontakt"
              className="px-8 py-4 rounded-full border border-white text-white hover:bg-white/10 font-bold text-base transition-all"
            >
              Udfyld tilbudsformular
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
